import { forwardRef } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, ChevronDown, MapPin, Box } from "lucide-react";
import { track } from "@/lib/analytics";

const Hero = forwardRef(function Hero(_, ref) {
    return (
        <section
            id="inicio"
            ref={ref}
            data-testid="hero-section"
            className="relative flex min-h-screen flex-col overflow-hidden supports-[height:100svh]:min-h-[100svh]"
        >
            <div className="mx-auto grid w-full max-w-7xl flex-1 items-center gap-8 px-6 pb-24 pt-28 md:pt-32 lg:grid-cols-2 lg:px-10">
                <div className="relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#059669]/25 bg-white/70 px-4 py-2 backdrop-blur-md"
                        data-testid="hero-fda-badge"
                    >
                        <ShieldCheck size={15} className="text-[#059669]" />
                        <span className="text-xs font-bold tracking-[0.18em] text-[#0A192F]">FDA 510(k)</span>
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 32 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.25 }}
                        className="font-display text-4xl font-extrabold leading-[1.02] tracking-tighter text-[#0A192F] sm:text-6xl lg:text-7xl"
                        data-testid="hero-headline"
                    >
                        TEST DE DROGAS
                        <br />
                        EN SALIVA{" "}
                        <span className="text-[#57A632]">
                            LYHER<span className="align-super text-[0.5em]">®</span>
                        </span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        className="mt-6 max-w-md text-base font-semibold text-[#0A192F] md:text-lg"
                        data-testid="hero-subtitle"
                    >
                        Tecnología de detección rápida en fluido oral.
                    </motion.p>

                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.5 }}
                        className="mt-3 max-w-md text-sm leading-relaxed text-slate-500 sm:text-base"
                    >
                        Detección cualitativa de múltiples drogas mediante un dispositivo rápido, visual y fácil de
                        utilizar.
                    </motion.p>

                    <div className="mt-8 h-[82svh] w-full lg:hidden" aria-hidden="true" data-testid="hero-product-window" />

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.65 }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <a
                            href="#producto"
                            data-testid="hero-cta-producto"
                            className="inline-flex items-center gap-2 rounded-full bg-[#0A192F] px-7 py-4 text-sm font-bold tracking-wide text-white transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-[#059669] hover:shadow-[0_12px_30px_-8px_rgba(5,150,105,0.5)]"
                        >
                            <Box size={16} />
                            CONOCER EL PRODUCTO
                        </a>
                        <a
                            href="#chile"
                            data-testid="hero-cta-chile"
                            onClick={() => track("click_find_chile", { cta_location: "hero", destination: "chile_section" })}
                            className="inline-flex items-center gap-2 rounded-full border border-[#0A192F]/15 bg-white px-7 py-4 text-sm font-bold tracking-wide text-[#0A192F] backdrop-blur-md transition-[border-color,color,transform] duration-300 hover:-translate-y-0.5 hover:border-[#059669] hover:text-[#059669] lg:bg-white/70"
                        >
                            <MapPin size={16} />
                            ENCONTRAR EN CHILE
                        </a>
                    </motion.div>
                </div>
                <div className="hidden lg:block" />
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 1 }}
                className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-3 sm:flex"
                data-testid="hero-scroll-indicator"
            >
                <span className="text-[11px] font-bold tracking-[0.22em] text-slate-400">
                    SCROLL PARA DESCUBRIR LA TECNOLOGÍA
                </span>
                <div className="flex h-10 w-6 items-start justify-center rounded-full border border-slate-300 p-1.5">
                    <div className="scroll-dot h-2 w-1 rounded-full bg-[#059669]" />
                </div>
                <ChevronDown size={16} className="text-slate-400" />
            </motion.div>
        </section>
    );
});

export default Hero;
